import type { AxiosRequestConfig } from 'axios'
import type { DataTableHeader } from 'vuetify/components/VDataTable'

import { createClient } from './client'
import { fetchNetSuiteSummary } from './generic-crud'
import type {
  NetSuiteSummaryData,
  NetSuiteFetchResponse,
  NetSuiteResponse,
  NetSuiteSearchResponse,
} from './types'

import type { DataModel } from '@/composables/useCsvValidation'
import type { RawNetSuiteItem, NetSuiteItemRecord, NetSuiteItem } from '@/types'
import { serializeFieldsAsJSON } from '@/utils'
import { transformRawNetSuiteItem } from '@/utils/transformers/generic'

const sortingRoute = '/sorting'
const traysRoute = '/trays'

export enum TRAY_STATUS_GROUPS {
  INVENTORY = 'inventory',
  SORTING = 'sorting',
  BUILDING = 'building',
  COMPLETE = 'complete',
}

export interface TraySearchParams {
  pageIndex?: number
  pageSize?: number
  sortColumn?: string
  sortDirection?: 'ASC' | 'DESC'
  id?: string
  trayBarcode?: string
  keyword?: string
  statusGroups?: TRAY_STATUS_GROUPS[]
  resultColumns?: string[]
}

export interface SortingPayloadItem<T = any> {
  barcode: string
  build: boolean
  blocks: T[]
}

export type SortingDataModel = DataModel

export interface SortingCreationResponse {
  status: 'success'
}

export type TrayGradedData = {
  barcode: string
  build: boolean
  blocks: TrayBlock[]
}

export type RawTrayData = {
  internalid: NetSuiteItemRecord[]
  type: NetSuiteItemRecord[]
  itemid: string
  quantityavailable: number
  quantitybackordered: number
  quantityonhand: number
  totalValue: number
  custitem_df_rough_tray_id: string
  custitem_df_rough_tray_barcode: string
  custitem_df_rough_tray_complete_date: string
  custitem_df_rough_lifecycle_status: NetSuiteItemRecord[]
  custitem_df_rough_production_name: string
  custitem_df_graded_data_json_blob: TrayGradedData
}
export type RawNetSuiteTray = RawNetSuiteItem<RawTrayData>
export type RawTray = NetSuiteItem<RawTrayData>

// expands object types one level deep
type Expand<T> = T extends infer O ? { [K in keyof O]: O[K] } : never

// expands object types recursively
type ExpandRecursively<T> = T extends object
  ? T extends infer O
    ? { [K in keyof O]: ExpandRecursively<O[K]> }
    : never
  : T

type ExpandedRawTrayData = ExpandRecursively<RawTray>

// Future shape of the tray after cleaning up prefixes and what not
// export interface Tray {
//   id: string
//   itemid: string
//   recordType: string
//   barcode: string
//   trayId: string
//   lifecycleStatus: NetSuiteItemRecord
//   productionName: string
//   quantityAvailable: number
//   quantityBackordered: number
//   quantityOnHand: number
//   totalValue: number
//   type: NetSuiteItemRecord
//   blocks: TrayBlock[]
// }
// type TrayItem = NetSuiteItem<Tray>

export interface TrayBlock {
  [key: string]: string | number | undefined
  guid: string
  barcode: string
  quality: string
  carats: number
  caratsConsumed?: number
  color?: string
  size?: string
  error?: string
}

export const traysHeadersMapping = {
  type: {
    title: 'Type',
    key: 'type',
  },
  id: {
    title: 'NetSuite Internal ID',
    key: 'internalid',
  },
  itemId: {
    title: 'Name',
    key: 'itemid',
  },
  quantityAvailable: {
    title: 'Quantity Available',
    key: 'quantityavailable',
  },
  quantityBackordered: {
    title: 'Quantity Backordered',
    key: 'quantitybackordered',
  },
  quantityOnHand: {
    title: 'Quantity On Hand',
    key: 'quantityonhand',
  },
  totalValue: {
    title: 'Total Value',
    key: 'totalvalue',
  },
  trayId: {
    title: 'Tray ID',
    key: 'custitem_df_rough_tray_id',
  },
  barcode: {
    title: 'Barcode',
    key: 'custitem_df_rough_tray_barcode',
  },
  completeDate: {
    title: 'Complete Date',
    key: 'custitem_df_rough_tray_complete_date',
  },
  lifecycleStatus: {
    title: 'Lifecycle Status',
    key: 'custitem_df_rough_lifecycle_status',
  },
  roughProductionName: {
    title: 'Rough Production Name',
    key: 'custitem_df_rough_production_name',
  },
  gradedDataJsonBlob: {
    title: 'Graded Data Blob',
    key: 'custitem_df_graded_data_json_blob',
  },
} as const satisfies Record<string, DataTableHeader>

/**
 * Columns to request NetSuite return when fetching stones
 */
export const traysColumns = Object.values(traysHeadersMapping).map(
  (map: DataTableHeader) => map.key
) as string[]

export const defaultStatusGroups = [
  // TRAY_STATUS_GROUPS.INVENTORY,
  TRAY_STATUS_GROUPS.SORTING,
  TRAY_STATUS_GROUPS.BUILDING,
  TRAY_STATUS_GROUPS.COMPLETE,
]

/**
 * SORTING
 */

const _fetchSummary = fetchNetSuiteSummary<NetSuiteSummaryData>
export const fetchTraySummary = _fetchSummary.bind(null, traysRoute)

/**
 * Load the firmly defined fields for the sorting data
 * @returns The data model for the sorting data
 */
export async function fetchSortingDataModel() {
  return (await createClient()).get<SortingDataModel>(
    `${sortingRoute}/data-model`
  )
}

/**
 * Create the actual sorting object
 */
export async function createSorting(
  sortingData: SortingPayloadItem<TrayBlock>
) {
  return (await createClient()).post<SortingCreationResponse>(
    sortingRoute,
    sortingData
  )
}

/**
 * Fetch trays from Netsuite
 */
export async function fetchTrays(
  params: TraySearchParams,
  config: AxiosRequestConfig = {}
) {
  // Serialize specific fields
  // https://github.com/diamond-foundry/netsuite-account-customization-sandbox-1/blob/97955b6cb4f355ece0c0299e8e3a3ce8890d956b/src/FileCabinet/SuiteScripts/df/diamond-lifecycle/modules/df-diamond-lifecycle-search.js#L53
  config.params = serializeFieldsAsJSON(params, [
    'statusGroups',
    'resultColumns',
  ])
  const { data: nsResponse, ...response } = await (
    await createClient()
  ).get<NetSuiteSearchResponse<RawNetSuiteTray>>(traysRoute, config)

  const trays = nsResponse.data.items.map(transformRawNetSuiteItem)

  return {
    response,
    pagination: nsResponse.data.info,
    data: trays,
  } as NetSuiteFetchResponse<RawTray[]>
}

export async function retryBuild(internalId: string) {
  const client = await createClient()
  const result = await client.safePost<
    NetSuiteResponse<{ tray_internal_id: number }>
  >(`${traysRoute}/retry-build`, {
    tray_internal_id: internalId,
  })

  if (result.error) return result
  const nsResponse = result.data

  return {
    ...result,
    data: nsResponse.data,
  }
}

export async function unbuildTray(internalId: string) {
  const client = await createClient()
  const result = await client.safePost<
    NetSuiteResponse<{
      workOrderIDs: number[]
      assemblyBuildIDs: number[]
    }>
  >(`${traysRoute}/unbuild`, {
    tray_internal_id: internalId,
  })

  if (result.error) return result
  const nsResponse = result.data

  return {
    ...result,
    data: nsResponse.data,
  }
}
